package one.devsbox.commons.ads

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.ResponseException
import io.ktor.client.plugins.auth.Auth
import io.ktor.client.plugins.auth.providers.BasicAuthCredentials
import io.ktor.client.plugins.auth.providers.basic
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.prepareGet
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.serialization.Serializable
import one.devsbox.commons.EMPTY
import one.devsbox.commons.JSON_INSTANCE
import one.devsbox.commons.nowDateTime
import one.devsbox.commons.plus
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.ads.CampaignState
import one.devsbox.shared.devices.ApplicationType
import one.devsbox.shared.devices.IO
import kotlin.time.Clock
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds
import one.devsbox.shared.files.SharedIO as SharedIOFiles
import io.ktor.client.plugins.websocket.WebSockets
import io.ktor.client.plugins.websocket.DefaultClientWebSocketSession
import io.ktor.client.plugins.websocket.ws
import io.ktor.client.plugins.websocket.wss
import io.ktor.websocket.Frame
import io.ktor.websocket.readText
import io.ktor.websocket.close
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import one.devsbox.shared.msg.MessageBody

@Target(AnnotationTarget.FUNCTION)
@Retention(AnnotationRetention.SOURCE)
annotation class RequiredPermission(val permission: String)

/**
 * Client API for ads operations.
 *
 * @param url The url for the API endpoint.
 */
class ApiAds(
    private val url: String,
    private var login: LoginData = LoginData(),
    private val onLoginChange: suspend (LoginData) -> Unit = {}
) {

    val token: String get() = login.token

    suspend fun validate() {
        if (login.login.isEmpty() || login.pass.isEmpty()) error("Unauthorized")
        if (Clock.nowDateTime() >= login.expires) {
            login = login(login.login, login.pass)
            onLoginChange(login)
        }
    }

    internal val clientJson
        get() = HttpClient {
            install(HttpTimeout) {
                requestTimeoutMillis = 30.seconds.inWholeMilliseconds
            }
            install(ContentNegotiation) {
                json(JSON_INSTANCE)
            }
        }

    internal val clientWS
        get() = HttpClient {
            install(WebSockets) {
                pingIntervalMillis = 30.seconds.inWholeMilliseconds
            }
        }

    @Serializable
    data class LoginData(
        var login: String = "",
        var pass: String = "",
        val userId: Long = -1,
        val token: String = "",
        val expires: LocalDateTime = LocalDateTime.EMPTY,
    ) {}

    interface User {
        @RequiredPermission("user.read")
        suspend fun users(page: Int): SharedIO.Users

        @RequiredPermission("user.read")
        suspend fun activity(id: Long, page: Int, action: Int): SharedIO.UserActivities

        @RequiredPermission("user.write")
        suspend fun add(user: SharedIO.UserNew): SharedIO.User

        @RequiredPermission("Public")
        suspend fun me(): SharedIO.User

        @RequiredPermission("user.read OR self")
        suspend fun get(id: Long): SharedIO.User

        @RequiredPermission("user.write OR self")
        suspend fun resetPassword(id: Long)

        @RequiredPermission("user.write OR self")
        suspend fun change(id: Long, user: SharedIO.UserNew)

        @RequiredPermission("user.delete")
        suspend fun delete(id: Long)

        @RequiredPermission("user.write OR self")
        suspend fun changePassword(id: Long, newPassword: String)

        @RequiredPermission("company.read")
        suspend fun switchCompany(companyId: Long)
    }

    interface Location {
        @RequiredPermission("location.read OR device.write")
        suspend fun listing(page: Int): SharedIO.Locations

        @RequiredPermission("location.read OR campaign.write")
        suspend fun listingWithDevices(page: Int): SharedIO.LocationsWithDevices

        @RequiredPermission("location.write")
        suspend fun add(location: SharedIO.LocationNew): SharedIO.Location

        @RequiredPermission("location.read")
        suspend fun get(id: Long): SharedIO.Location

        @RequiredPermission("location.write")
        suspend fun change(id: Long, location: SharedIO.LocationNew)

        @RequiredPermission("location.delete")
        suspend fun delete(id: Long)
    }

    interface Media {
        @RequiredPermission("media.read OR campaign.write OR device.write")
        suspend fun listing(page: Int): SharedIO.Resources

        @RequiredPermission("media.read OR campaign.write OR device.write")
        suspend fun get(id: Long): SharedIO.Media

        @RequiredPermission("media.write")
        suspend fun change(
            id: Long,
            properties: SharedIOFiles.VisualPropertiesIn
        ): SharedIO.Media

        @RequiredPermission("media.delete")
        suspend fun delete(id: Long)

        @RequiredPermission("media.read OR campaign.write OR device.write")
        suspend fun filter(type: Int, rotation: Int, page: Int): SharedIO.Resources
    }

    interface Device {
        @RequiredPermission("device.read OR campaign.write")
        suspend fun listing(page: Int): SharedIO.Devices

        @RequiredPermission("device.write")
        suspend fun register(type: ApplicationType, locationId: Long): IO.Registration

        @RequiredPermission("device.read")
        suspend fun get(id: Long): SharedIO.Device

        @RequiredPermission("device.write")
        suspend fun change(id: Long, change: SharedIO.DeviceChange)

        @RequiredPermission("device.delete")
        suspend fun delete(id: Long)

        @RequiredPermission("device.read")
        suspend fun screenshot(id: Long): ByteArray

        @RequiredPermission("device.write")
        suspend fun reboot(id: Long)

        @RequiredPermission("device.write")
        suspend fun updateWww(id: Long)

        @RequiredPermission("device.write")
        suspend fun updateRuntime(id: Long)

        @RequiredPermission("device.read")
        suspend fun historyAlive(id: Long, page: Int): List<IO.Alive>

        @RequiredPermission("device.read")
        suspend fun historyMonitor(id: Long, page: Int): List<IO.Monitoring>

        @RequiredPermission("device.write")
        fun cmd(deviceId: Long): CmdController
    }

    interface Campaign {
        @RequiredPermission("campaign.read")
        suspend fun listing(state: Int, page: Int, date: LocalDate): SharedIO.Campaigns

        @RequiredPermission("campaign.read")
        suspend fun listingByDeviceId(deviceId: Long, page: Int): SharedIO.Campaigns

        @RequiredPermission("campaign.write")
        suspend fun add(campaign: SharedIO.CampaignCore): String

        @RequiredPermission("campaign.write")
        suspend fun regenerate()

        @RequiredPermission("campaign.write")
        suspend fun change(id: Long, campaign: SharedIO.CampaignCore)

        @RequiredPermission("campaign.write")
        suspend fun change(id: Long, state: CampaignState): SharedIO.CampaignInfo

        @RequiredPermission("campaign.read")
        suspend fun get(id: Long): SharedIO.Campaign

        @RequiredPermission("campaign.read")
        suspend fun info(id: Long): SharedIO.CampaignInfo

        @RequiredPermission("campaign.delete")
        suspend fun delete(id: Long)
    }

    interface App {
        @RequiredPermission("Public")
        suspend fun listing(): List<IO.Application>

        @RequiredPermission("Public")
        suspend fun <T> download(id: Long, block: suspend (HttpResponse) -> T): T

        @RequiredPermission("Public")
        suspend fun set(id: Long, version: Int, data: ByteArray)
    }

    interface Company {
        @RequiredPermission("company.read")
        suspend fun listing(page: Int): SharedIO.Companies

        @RequiredPermission("company.write")
        suspend fun add(name: String)

        @RequiredPermission("company.write")
        suspend fun rename(id: Long, name: String)

        @RequiredPermission("company.delete")
        suspend fun delete(id: Long)
    }

    suspend fun login(name: String, pass: String): LoginData = HttpClient {
        install(HttpTimeout) {
            requestTimeoutMillis = 30.seconds.inWholeMilliseconds
        }
        install(Auth) {
            basic {
                credentials {
                    BasicAuthCredentials(username = name, password = pass)
                }
            }
        }
    }.use {
        val respond = it.post("$url/api/login").throwOnFail()

        LoginData(
            login = name,
            pass = pass,
            userId = respond.bodyAsText().toLong(),
            token = respond.headers[HttpHeaders.LockToken]!!,
            expires = Clock.nowDateTime()
                .plus(respond.headers[HttpHeaders.Expires]!!.toLong().milliseconds)
        )
    }

    val user = object : User {
        override suspend fun users(page: Int): SharedIO.Users {
            validate()
            return clientJson.get("$url/api/user/page/$page") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun activity(id: Long, page: Int, action: Int): SharedIO.UserActivities {
            validate()
            return clientJson.get("$url/api/user/$id/activity/$page/$action") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun add(user: SharedIO.UserNew): SharedIO.User {
            validate()
            return clientJson.put("$url/api/user") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
                contentType(ContentType.Application.Json)
                setBody(user)
            }.throwOnFail().body()
        }

        override suspend fun me(): SharedIO.User {
            validate()
            return clientJson.get("$url/api/user/me") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun get(id: Long): SharedIO.User {
            validate()
            return clientJson.get("$url/api/user/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }


        override suspend fun change(id: Long, user: SharedIO.UserNew) {
            validate()
            clientJson.post("$url/api/user/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
                contentType(ContentType.Application.Json)
                setBody(user)
            }.throwOnFail()
        }

        override suspend fun delete(id: Long) {
            validate()
            clientJson.delete("$url/api/user/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }

        override suspend fun changePassword(id: Long, newPassword: String) {
            clientJson.post("$url/api/user/$id/passwd") {
                contentType(ContentType.Text.Any)
                setBody(newPassword)
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }

        override suspend fun resetPassword(id: Long) {
            clientJson.delete("$url/api/user/$id/passwd") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }

        override suspend fun switchCompany(companyId: Long) {
            validate()
            clientJson.post("$url/api/user/switch/$companyId") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }
    }

    val location = object : Location {
        override suspend fun listing(page: Int): SharedIO.Locations {
            validate()
            return clientJson.get("$url/api/location/page/$page") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun listingWithDevices(page: Int): SharedIO.LocationsWithDevices {
            validate()
            return clientJson.get("$url/api/location/page/$page/devices") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun add(location: SharedIO.LocationNew): SharedIO.Location {
            validate()
            return clientJson.put("$url/api/location") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
                contentType(ContentType.Application.Json)
                setBody(location)
            }.throwOnFail().body()
        }

        override suspend fun get(id: Long): SharedIO.Location {
            validate()
            return clientJson.get("$url/api/location/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun change(id: Long, location: SharedIO.LocationNew) {
            validate()
            clientJson.post("$url/api/location/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
                contentType(ContentType.Application.Json)
                setBody(location)
            }.throwOnFail()
        }

        override suspend fun delete(id: Long) {
            validate()
            clientJson.delete("$url/api/location/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }
    }

    val media = object : Media {
        override suspend fun listing(page: Int): SharedIO.Resources {
            validate()
            return clientJson.get("$url/api/media/page/$page") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun get(id: Long): SharedIO.Media {
            validate()
            return clientJson.get("$url/api/media/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun change(
            id: Long,
            properties: SharedIOFiles.VisualPropertiesIn
        ): SharedIO.Media {
            validate()
            return clientJson.patch("$url/api/media/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
                contentType(ContentType.Application.Json)
                setBody(properties)
            }.throwOnFail().body()
        }

        override suspend fun delete(id: Long) {
            validate()
            clientJson.delete("$url/api/media/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }

        override suspend fun filter(
            type: Int,
            rotation: Int,
            page: Int
        ): SharedIO.Resources {
            validate()
            return clientJson.get("$url/api/media/filter/$type/$rotation/$page") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }
    }

    val device = object : Device {
        override suspend fun listing(page: Int): SharedIO.Devices {
            validate()
            return clientJson.get("$url/api/device/page/$page") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun register(type: ApplicationType, locationId: Long): IO.Registration {
            validate()
            return clientJson.post("$url/api/device/register/${type.mask}/$locationId") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun get(id: Long): SharedIO.Device {
            validate()
            return clientJson.get("$url/api/device/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun change(id: Long, change: SharedIO.DeviceChange) {
            validate()
            clientJson.post("$url/api/device/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
                contentType(ContentType.Application.Json)
                setBody(change)
            }.throwOnFail()
        }

        override suspend fun delete(id: Long) {
            validate()
            clientJson.delete("$url/api/device/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }

        override suspend fun screenshot(id: Long): ByteArray {
            validate()
            return clientJson.post("$url/api/device/$id/command/screenshot") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun reboot(id: Long) {
            validate()
            clientJson.post("$url/api/device/$id/command/reboot") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }

        override suspend fun updateWww(id: Long) {
            validate()
            clientJson.post("$url/api/device/$id/command/update/www") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }

        override suspend fun updateRuntime(id: Long) {
            validate()
            clientJson.post("$url/api/device/$id/command/update/runtime") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }

        override suspend fun historyAlive(id: Long, page: Int): List<IO.Alive> {
            validate()
            return clientJson.get("$url/api/device/$id/history/alive/$page") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun historyMonitor(id: Long, page: Int): List<IO.Monitoring> {
            validate()
            return clientJson.get("$url/api/device/$id/history/monitor/$page") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override fun cmd(deviceId: Long): CmdController {
            return CmdControllerImpl(deviceId, clientWS, url, token)
        }
    }

    val campaign = object : Campaign {
        override suspend fun listing(state: Int, page: Int, date: LocalDate): SharedIO.Campaigns {
            validate()
            return clientJson.get("$url/api/campaign/state/$state/page/$page/date/$date") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun listingByDeviceId(deviceId: Long, page: Int): SharedIO.Campaigns {
            validate()
            return clientJson.get("$url/api/campaign/device/$deviceId/page/$page") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun add(campaign: SharedIO.CampaignCore): String {
            validate()
            return clientJson.put("$url/api/campaign") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
                contentType(ContentType.Application.Json)
                setBody(campaign)
            }.throwOnFail().bodyAsText()
        }

        override suspend fun regenerate() {
            validate()
            clientJson.post("$url/api/campaign/regenerate") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }

        override suspend fun change(id: Long, campaign: SharedIO.CampaignCore) {
            validate()
            clientJson.post("$url/api/campaign/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
                contentType(ContentType.Application.Json)
                setBody(campaign)
            }.throwOnFail()
        }

        override suspend fun change(id: Long, state: CampaignState): SharedIO.CampaignInfo {
            validate()
            return clientJson.patch("$url/api/campaign/$id/state/${state.mask}") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun get(id: Long): SharedIO.Campaign {
            validate()
            return clientJson.get("$url/api/campaign/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun info(id: Long): SharedIO.CampaignInfo {
            validate()
            return clientJson.get("$url/api/campaign/$id/info") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun delete(id: Long) {
            validate()
            clientJson.delete("$url/api/campaign/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }
    }

    val app = object : App {
        override suspend fun listing(): List<IO.Application> {
            validate()
            return clientJson.get("$url/api/app") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun <T> download(id: Long, block: suspend (HttpResponse) -> T): T {
            validate()
            return clientJson.prepareGet("$url/api/app/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.execute { response ->
                response.throwOnFail()
                block(response)
            }
        }

        override suspend fun set(id: Long, version: Int, data: ByteArray) {
            validate()
            clientJson.put("$url/api/app/$id/$version") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
                setBody(data)
            }.throwOnFail()
        }
    }

    val company = object : Company {
        override suspend fun listing(page: Int): SharedIO.Companies {
            validate()
            return clientJson.get("$url/api/company/page/$page") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail().body()
        }

        override suspend fun add(name: String) {
            validate()
            clientJson.post("$url/api/company") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
                contentType(ContentType.Text.Plain)
                setBody(name)
            }.throwOnFail()
        }

        override suspend fun rename(id: Long, name: String) {
            validate()
            clientJson.patch("$url/api/company/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
                contentType(ContentType.Text.Plain)
                setBody(name)
            }.throwOnFail()
        }

        override suspend fun delete(id: Long) {
            validate()
            clientJson.delete("$url/api/company/$id") {
                header(HttpHeaders.Authorization, "Bearer ${login.token}")
            }.throwOnFail()
        }
    }
}

/**
 * Validates the HTTP response and throws an exception if the status is not successful.
 */
private fun HttpResponse.throwOnFail(): HttpResponse {
    if (status.value != HttpStatusCode.OK.value) throw ResponseException(this, status.description)
    return this
}

interface CmdController {
    suspend fun send(msg: String)
    suspend fun reset()
    fun reconnect()
    fun close()
    fun onMessage(body: (MessageBody.Cmd) -> Unit)
    fun onClose(body: () -> Unit)
    fun onOpen(body: () -> Unit)
    fun onError(body: (Throwable) -> Unit)
}

class CmdControllerImpl(
    private val deviceId: Long,
    private val client: HttpClient,
    private val baseUrl: String,
    private val token: String
) : CmdController {
    private var session: DefaultClientWebSocketSession? = null
    private var job: Job? = null
    private val scope = CoroutineScope(Dispatchers.Default + Job())
    private var messageListener: ((MessageBody.Cmd) -> Unit)? = null
    private var closeListener: (() -> Unit)? = null
    private var openListener: (() -> Unit)? = null
    private var errorListener: ((Throwable) -> Unit)? = null

    init {
        connect()
    }

    private fun connect() {
        job?.cancel()
        job = scope.launch {
            val wsUrl = buildString {
                if (baseUrl.startsWith("https://")) {
                    append(baseUrl.replace("https://", "wss://"))
                } else if (baseUrl.startsWith("http://")) {
                    append(baseUrl.replace("http://", "ws://"))
                } else {
                    append("ws://").append(baseUrl)
                }
                append("/api/device/").append(deviceId).append("/cmd")
            }

            val block: suspend DefaultClientWebSocketSession.() -> Unit = {
                session = this
                send(token)
                openListener?.invoke()
                try {
                    for (frame in incoming) {
                        if (frame is Frame.Text) {
                            runCatching {
                                val json = frame.readText()
                                val cmd = JSON_INSTANCE.decodeFromString<MessageBody.Cmd>(json)
                                messageListener?.invoke(cmd)
                            }.onFailure { it.printStackTrace() }
                        } else if (frame is Frame.Close) {
                            break
                        }
                    }
                } finally {
                    session = null
                    closeListener?.invoke()
                }
            }

            runCatching {
                if (wsUrl.startsWith("wss://")) {
                    client.wss(
                        urlString = wsUrl,
                        request = { header("Authorization", "Bearer $token") },
                        block = block
                    )
                } else {
                    client.ws(
                        urlString = wsUrl,
                        request = { header("Authorization", "Bearer $token") },
                        block = block
                    )
                }
            }.onFailure {
                errorListener?.invoke(it)
            }
        }
    }

    override suspend fun send(msg: String) {
        session?.send(Frame.Text(msg))
    }

    override suspend fun reset() {
        session?.send(Frame.Text(""))
    }

    override fun reconnect() {
        connect()
    }

    override fun close() {
        scope.launch {
            runCatching {
                session?.close()
            }
            job?.cancel()
        }
    }

    override fun onMessage(body: (MessageBody.Cmd) -> Unit) {
        messageListener = body
    }

    override fun onClose(body: () -> Unit) {
        closeListener = body
    }

    override fun onOpen(body: () -> Unit) {
        openListener = body
    }

    override fun onError(body: (Throwable) -> Unit) {
        errorListener = body
    }
}
